import {
	ApplicationInsights,
	DistributedTracingModes,
} from '@microsoft/applicationinsights-web';

export type TelemetryValue = string | number | boolean;
export type TelemetryProperties = Record<string, TelemetryValue | null | undefined>;
export type TelemetryDeviceType = 'desktop' | 'laptop' | 'mobile';

const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING?.trim() ?? '';
const excludedDependencyHostSuffixes = [
	'google.com',
	'google-analytics.com',
	'googleadservices.com',
	'googlesyndication.com',
	'doubleclick.net',
	'linkedin.com',
];

let appInsightsInstance: ApplicationInsights | null = null;

export const isAppInsightsEnabled = (): boolean => connectionString.length > 0;

export const initializeAppInsights = (): ApplicationInsights | null => {
	if (!isAppInsightsEnabled()) {
		return null;
	}

	if (appInsightsInstance) {
		return appInsightsInstance;
	}

	appInsightsInstance = new ApplicationInsights({
		config: {
			connectionString,
			disableAjaxTracking: false,
			disableFetchTracking: false,
			distributedTracingMode: DistributedTracingModes.W3C,
		},
	});

	appInsightsInstance.addTelemetryInitializer((item) => {
		if (item.baseType !== 'RemoteDependencyData') {
			return true;
		}

		const baseData = item.baseData as {
			data?: string;
			name?: string;
			target?: string;
		} | undefined;

		const dependencyTarget = [
			baseData?.target,
			baseData?.data,
			baseData?.name,
		]
			.filter((value): value is string => Boolean(value))
			.join(' ')
			.toLowerCase();

		if (!dependencyTarget) {
			return true;
		}

		const shouldDropDependency = excludedDependencyHostSuffixes.some(
			(hostSuffix) =>
				dependencyTarget === hostSuffix ||
				dependencyTarget.includes(`.${hostSuffix}`) ||
				dependencyTarget.includes(hostSuffix)
		);

		return shouldDropDependency ? false : true;
	});

	appInsightsInstance.loadAppInsights();

	return appInsightsInstance;
};

const normalizeTelemetryProperties = (
	properties?: TelemetryProperties
): Record<string, string> | undefined => {
	if (!properties) {
		return undefined;
	}

	const normalizedEntries = Object.entries(properties)
		.filter(([, value]) => value !== undefined && value !== null && value !== '')
		.map(([key, value]) => [key, String(value)] as const);

	if (normalizedEntries.length === 0) {
		return undefined;
	}

	return Object.fromEntries(normalizedEntries);
};

export const emitAnalytics = (name: string, properties?: TelemetryProperties): void => {
	if (!appInsightsInstance) {
		return;
	}

	appInsightsInstance.trackEvent({
		name,
		properties: normalizeTelemetryProperties(properties),
	});
};

export const flushAnalytics = (): void => {
	if (!appInsightsInstance) {
		return;
	}

	appInsightsInstance.flush();
};

export const trackPageView = (name: string, uri: string): void => {
	if (!appInsightsInstance) {
		return;
	}
	appInsightsInstance.trackPageView({
		name,
		uri,
	});
};

export const trackEvent = (name: string, properties?: TelemetryProperties): void => {
	emitAnalytics(name, properties);
};

export const getTelemetryDeviceType = (): TelemetryDeviceType => {
	if (typeof window === 'undefined') {
		return 'desktop';
	}

	const { innerWidth } = window;

	if (innerWidth <= 768) {
		return 'mobile';
	}

	if (innerWidth >= 1660) {
		return 'desktop';
	}

	return 'laptop';
};
